export const policies = {
  "privacy-policy": {
    title: "Privacy Policy",
    effectiveDate: "July 1, 2026",
    intro:
      "ABA Virtual values your privacy and is committed to protecting your personal information.",
    sections: [
      {
        heading: "Information We Collect",
        paragraphs: ["We may collect:"],
        list: [
          "Name",
          "Email address",
          "Phone number",
          "Country of residence",
          "Information submitted through enquiry forms",
          "Information shared during consultations or assessments",
          "Payment-related information (processed securely through third-party payment providers)",
        ],
        afterList:
          "ABA Virtual does not store your payment card information.",
      },
      {
        heading: "How We Use Information",
        paragraphs: ["Your information may be used to:"],
        list: [
          "Provide consultation services",
          "Schedule appointments",
          "Deliver digital resources",
          "Respond to enquiries",
          "Improve our services",
          "Communicate important updates",
        ],
      },
      {
        heading: "Confidentiality",
        paragraphs: [
          "Information shared during consultations is treated confidentially and is only disclosed where required by applicable law or with your consent.",
        ],
      },
      {
        heading: "Cookies",
        paragraphs: [
          "Our website may use cookies and analytics tools to improve website performance and user experience.",
          "You may disable cookies through your browser settings.",
        ],
      },
      {
        heading: "Third-Party Services",
        paragraphs: ["We may use trusted third-party providers for:"],
        list: [
          "Payment processing",
          "Website hosting",
          "Email communications",
          "Analytics",
        ],
        afterList:
          "These providers process information in accordance with their own privacy policies.",
      },
      {
        heading: "Data Security",
        paragraphs: [
          "Reasonable administrative, technical, and organisational measures are implemented to protect personal information from unauthorised access, alteration, or disclosure.",
        ],
      },
      {
        heading: "Your Rights",
        paragraphs: ["You may request to:"],
        list: [
          "Access your personal information",
          "Correct inaccurate information",
          "Request deletion where legally permissible",
          "Withdraw consent for future communications",
        ],
      },
      {
        heading: "Policy Updates",
        paragraphs: [
          "This Privacy Policy may be updated periodically. Any revisions will be published on this page with an updated effective date.",
        ],
      },
    ],
    contactEmail: "contact@abavirtual.net",
  },
  "refund-policy": {
    title: "Refund Policy",
    effectiveDate: "July 1, 2026",
    intro:
      "ABA Virtual strives to provide high-quality professional services and educational resources.",
    sections: [
      {
        heading: "Consultation & Therapy Sessions",
        paragraphs: [
          "Appointments cancelled at least 24 hours before the scheduled session may be rescheduled without additional charges.",
          "Appointments missed without prior notice may not be eligible for a refund.",
        ],
      },
      {
        heading: "Assessments",
        paragraphs: [
          "Once an assessment has commenced, refunds are generally not available because professional time has already been allocated.",
        ],
      },
      {
        heading: "Digital Products",
        paragraphs: [
          "Due to the instant delivery of downloadable resources, PDFs, guides, worksheets, and similar digital products are non-refundable.",
          "If a purchased file is corrupted or inaccessible, we will provide a replacement copy.",
        ],
      },
      {
        heading: "Exceptional Circumstances",
        paragraphs: [
          "Refund requests resulting from duplicate payments, technical errors, or billing mistakes will be reviewed individually.",
          "Where approved, refunds will be processed using the original payment method.",
        ],
      },
      {
        heading: "Processing Time",
        paragraphs: [
          "Approved refunds are generally processed within 7–14 business days depending on the payment provider.",
        ],
      },
    ],
    contactEmail: "contact@abavirtual.net",
  },
  "terms-and-conditions": {
    title: "Terms & Conditions",
    effectiveDate: "July 1, 2026",
    intro:
      'Welcome to ABA Virtual ("we", "our", "us"). By accessing or using www.abavirtual.net, booking our services, or purchasing our digital resources, you agree to these Terms & Conditions.',
    sections: [
      {
        heading: "1. Our Services",
        paragraphs: [
          "ABA Virtual provides remote Applied Behavior Analysis (ABA) consultation, caregiver coaching, therapist supervision, professional training, behavioural assessments, intervention planning, and downloadable educational resources.",
          "Our services are educational and consultative in nature and are not intended to replace medical diagnosis, emergency care, or treatment provided by licensed healthcare professionals.",
        ],
      },
      {
        heading: "2. Eligibility",
        paragraphs: [
          "By using this website, you confirm that you are at least 18 years of age or are acting on behalf of a minor with appropriate legal authority.",
        ],
      },
      {
        heading: "3. Appointments",
        paragraphs: [
          "Appointments are scheduled based on mutual availability.",
          "Clients are responsible for attending sessions on time and ensuring access to a stable internet connection and suitable device for virtual sessions.",
        ],
      },
      {
        heading: "4. Payments",
        paragraphs: [
          "All payments must be completed before consultations, supervision sessions, assessments, or digital resources are delivered unless otherwise agreed in writing.",
          "Payments are processed securely through approved payment providers.",
        ],
      },
      {
        heading: "5. Digital Resources",
        paragraphs: [
          "All downloadable materials, guides, worksheets, and educational content remain the intellectual property of ABA Virtual.",
          "Resources are licensed for personal use only.",
          "You may not:",
        ],
        list: [
          "Copy",
          "Reproduce",
          "Redistribute",
          "Resell",
          "Upload",
          "Share publicly",
        ],
        afterList: "without written permission.",
      },
      {
        heading: "6. Intellectual Property",
        paragraphs: [
          "All website content, including text, graphics, logos, images, documents, training materials, videos, and downloadable resources, belongs to ABA Virtual unless otherwise stated.",
        ],
      },
      {
        heading: "7. Client Responsibilities",
        paragraphs: ["Clients agree to:"],
        list: [
          "Provide accurate information.",
          "Participate honestly during assessments.",
          "Follow professional recommendations at their own discretion.",
          "Maintain respectful communication.",
        ],
      },
      {
        heading: "8. Results Disclaimer",
        paragraphs: [
          "Every learner is unique.",
          "While ABA Virtual applies evidence-based practices, we cannot guarantee specific behavioural or developmental outcomes.",
          "Progress depends on many factors, including consistency, implementation, environment, learner characteristics, and caregiver participation.",
        ],
      },
      {
        heading: "9. Limitation of Liability",
        paragraphs: [
          "ABA Virtual shall not be liable for indirect, incidental, or consequential losses arising from the use of our website, services, or digital resources.",
          "Our liability shall never exceed the amount paid for the specific service or product giving rise to the claim.",
        ],
      },
      {
        heading: "10. Privacy",
        paragraphs: [
          "Your use of this website is also governed by our Privacy Policy.",
        ],
      },
      {
        heading: "11. Changes",
        paragraphs: [
          "ABA Virtual may update these Terms & Conditions at any time without prior notice. Continued use of the website constitutes acceptance of any revised terms.",
        ],
      },
      {
        heading: "12. Governing Law",
        paragraphs: [
          "These Terms & Conditions shall be governed by the laws of the Islamic Republic of Pakistan.",
        ],
      },
    ],
    contactEmail: "contact@abavirtual.net",
  },
  "service-delivery-policy": {
    title: "Service Delivery Policy",
    effectiveDate: "July 1, 2026",
    intro:
      "ABA Virtual provides professional services remotely and delivers digital educational resources electronically. This Service Delivery Policy explains how and when our services and products are provided.",
    sections: [
      {
        heading: "1. Virtual Consultations",
        paragraphs: [
          "All consultations, coaching sessions, supervision, assessments, and training are conducted online through mutually agreed virtual communication platforms.",
          "Appointment details and meeting links will be shared with clients after successful booking and payment confirmation.",
        ],
      },
      {
        heading: "2. Appointment Scheduling",
        paragraphs: [
          "Appointments are scheduled according to mutual availability.",
          "Clients will receive confirmation of their appointment via email or other agreed communication channels.",
          "If a client needs to reschedule, we request at least 24 hours' notice where reasonably possible.",
        ],
      },
      {
        heading: "3. Assessment & Intervention Services",
        paragraphs: [
          "Behavioural assessments, caregiver coaching, intervention planning, Individualized Education Programs (IEPs), Behaviour Intervention Plans (BIPs), Individualized Care Plans (ICPs), and related reports are delivered within the timeframe communicated during the consultation process.",
          "Delivery timelines may vary depending on the complexity of the assessment and the timely submission of required information by caregivers.",
        ],
      },
      {
        heading: "4. Digital Resources",
        paragraphs: [
          "Downloadable resources, including guidebooks, worksheets, and educational materials, are delivered electronically.",
          "Following successful payment, customers will receive access to their purchased digital product through the website, email, or a secure download link, depending on the product.",
        ],
      },
      {
        heading: "5. Delivery Time",
        paragraphs: [
          "Consultation bookings are typically confirmed within 1–2 business days.",
          "Digital resources are generally made available immediately after successful payment or within 24 hours if manual verification is required.",
          "Assessment reports and intervention plans are delivered within the timeframe communicated to the client during the assessment process.",
        ],
      },
      {
        heading: "6. Service Availability",
        paragraphs: [
          "Our services are available internationally. Delivery times may vary depending on time zone differences, public holidays, and scheduling availability.",
        ],
      },
      {
        heading: "7. Delays",
        paragraphs: [
          "While ABA Virtual strives to meet all communicated timelines, occasional delays may occur due to unforeseen circumstances, technical issues, or incomplete information provided by clients.",
          "If a delay is expected, clients will be informed as soon as reasonably possible.",
        ],
      },
      {
        heading: "8. Client Responsibilities",
        paragraphs: ["Clients are responsible for:"],
        list: [
          "Providing accurate contact information.",
          "Ensuring a stable internet connection for virtual sessions.",
          "Attending scheduled appointments on time.",
          "Providing requested documents or information promptly to avoid delays.",
        ],
      },
      {
        heading: "9. Technical Issues",
        paragraphs: [
          "If a session is interrupted due to technical difficulties beyond either party's control, ABA Virtual will make reasonable efforts to reschedule the remaining session at no additional cost.",
        ],
      },
    ],
    contactEmail: "contact@abavirtual.net",
    contactNote:
      "We aim to respond to all enquiries within 1–2 business days.",
  },
};
